<template>
  <div class="py-12 space-y-6">
    <!-- Actions : approuver, rejeter, supprimer -->
    <div class="flex gap-3 justify-end flex-wrap items-center">
      <template v-if="!isEditing">
        <button
          class="buttonComponent small border rounded-xl bg-gray-100 text-gray-700 font-bold"
          @click="startEditing"
        >
          Modifier
        </button>
        <template v-if="sponsorshipData?.status === 'pending' || sponsorshipData?.status === 'correction_suggested'">
          <button
            class="buttonComponent small border rounded-xl bg-success-600 text-white font-bold"
            @click="openActionModal('accept')"
          >
            Approuver
          </button>
          <!-- <button
            class="buttonComponent small border rounded-xl bg-orange-600 text-white font-bold"
            @click="openActionModal('suggest')"
          >
            Suggérer des corrections
          </button> -->
          <button
            class="buttonComponent small border rounded-xl bg-red-600 text-white font-bold"
            @click="openActionModal('reject')"
          >
            Rejeter & suggérer
          </button>
        </template>
        <template v-if="sponsorshipData?.status === 'active' || sponsorshipData?.status === 'approved'">
          <button
            class="buttonComponent small border rounded-xl bg-red-600 text-white font-bold"
            @click="openActionModal('block')"
          >
            Bloquer
          </button>
        </template>
        <template v-if="sponsorshipData?.status === 'blocked'">
          <button
            class="buttonComponent small border rounded-xl bg-orange-600 text-white font-bold"
            @click="openActionModal('unblock')"
          >
            Débloquer
          </button>
        </template>
        <button
          class="buttonComponent small border rounded-xl bg-red-600 text-white font-bold"
          @click="openActionModal('delete')"
        >
          Supprimer
        </button>
      </template>

      <template v-else>
        <button
          class="buttonComponent small border rounded-xl bg-gray-200 text-gray-700 font-bold"
          @click="cancelEditing"
        >
          Annuler
        </button>
        <button
          class="buttonComponent small border rounded-xl bg-primary-600 text-white font-bold pr-4 pl-4"
          @click="saveEditing"
        >
          Enregistrer
        </button>
      </template>
    </div>

    <!-- Header : Restaurant et Statut -->
    <div
      class="rounded-2xl border border-gray-200 bg-white p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
    >
      <div class="flex flex-col sm:flex-row items-start sm:items-center gap-6 w-full">
        <div class="flex-1">
          <div class="flex items-center gap-3">
            <h1 class="text-2xl font-bold title-color shimmerTarget">
              {{ sponsorshipData?.storeName || 'Sponsorisation' }}
            </h1>
            <span 
              :class="statusClasses"
              class="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
            >
              {{ statusLabel }}
            </span>
          </div>
          <p class="text-xs text-gray-400 mt-1 uppercase tracking-widest font-mono">ID: {{ sponsorshipData?.id }}</p>
          <div class="mt-4">
            <span class="text-4xl font-black text-primary-600 tracking-tighter">
              {{ formatAmount(sponsorshipData?.amount) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Période -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-6">
        Paramètres du sponsoring
      </h3>
      
      <div class="space-y-8">
        <!-- Période -->
        <div>
          <label class="text-xs text-gray-500 mb-3 block uppercase font-bold tracking-tight">Période de validité</label>
          <div v-if="!isEditing" class="flex items-center gap-12 bg-gray-50 p-4 rounded-xl border border-gray-100">
            <div>
              <div class="text-[10px] text-gray-400 uppercase mb-1">Date de début</div>
              <div class="font-bold text-gray-800 text-lg">{{ formatDate(sponsorshipData?.startDate) }}</div>
            </div>
            <div class="h-10 w-px bg-gray-200"></div>
            <div>
              <div class="text-[10px] text-gray-400 uppercase mb-1">Date de fin</div>
              <div class="font-bold text-gray-800 text-lg">{{ formatDate(sponsorshipData?.endDate) }}</div>
            </div>
          </div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="flex flex-col gap-2">
              <label class="text-xs text-gray-400 uppercase font-semibold">Date de début</label>
              <input
                type="date"
                v-model="editForm.startDate"
                class="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-all"
              />
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-xs text-gray-400 uppercase font-semibold">Date de fin</label>
              <input
                type="date"
                v-model="editForm.endDate"
                class="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-all"
              />
            </div>
          </div>
        </div>

        <!-- Bannière -->
        <div>
          <div class="flex items-center justify-between mb-3">
            <label class="text-xs text-gray-500 uppercase font-bold tracking-tight">Visuel du sponsoring</label>
            <button 
              v-if="!isEditing && !isEditingBanner" 
              @click="isEditingBanner = true"
              class="text-xs text-primary-600 font-bold hover:underline"
            >
              Modifier l'image uniquement
            </button>
            <button 
              v-if="isEditingBanner" 
              @click="isEditingBanner = false"
              class="text-xs text-gray-500 font-bold hover:underline"
            >
              Annuler la modification d'image
            </button>
          </div>

          <div v-if="!isEditing && !isEditingBanner" class="relative group">
            <div class="w-full h-48 rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 shadow-inner">
              <ImgComponent
                :src="sponsorshipData?.bannerImageUrl"
                fallback="/images/logo.png"
                alt="Bannière"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div class="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 pointer-events-none rounded-2xl"></div>
          </div>
          <div v-else class="w-full">
            <FileUploadWidget
              :defaultUrls="editForm.bannerImageUrl ? [editForm.bannerImageUrl] : (sponsorshipData?.bannerImageUrl ? [sponsorshipData.bannerImageUrl] : [])"
              :maxFiles="1"
              :acceptedFiles="['image/jpeg', 'image/png', 'image/webp']"
              @saved="handleImageSaved"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Motif de rejet (si applicable) -->
    <div v-if="sponsorshipData?.rejectionReason" class="rounded-2xl border border-red-100 bg-red-50 p-6">
       <h3 class="text-sm font-semibold text-red-600 uppercase tracking-wider mb-2">Motif du rejet</h3>
       <p class="text-red-800 font-medium">{{ sponsorshipData.rejectionReason }}</p>
    </div>

    <!-- Message de suggestion de correction (si applicable) -->
    <div v-if="sponsorshipData?.adminMessage" class="rounded-2xl border border-blue-100 bg-blue-50 p-6">
       <h3 class="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-2">Corrections demandées</h3>
       <p class="text-blue-800 font-medium">{{ sponsorshipData.adminMessage }}</p>
    </div>

    <!-- Restaurant Info -->
    <div class="rounded-2xl border border-gray-200 bg-gray-50 p-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">
        Détails du Restaurant
      </h3>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div class="text-sm text-gray-500">Nom de l'enseigne</div>
          <div class="font-medium text-gray-800">{{ sponsorshipData?.storeName }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-500">ID Restaurant</div>
          <div class="font-mono text-[10px] bg-white rounded p-2 mt-1 border border-gray-100">{{ sponsorshipData?.storeId }}</div>
        </div>
      </div>
    </div>

    <!-- Action Modals -->
    <SponsorshipActionModal
      :isVisible="actionModal.isVisible"
      :title="actionModal.title"
      :subtitle="actionModal.subtitle"
      :mode="actionModal.mode"
      :confirmText="actionModal.confirmText"
      :confirmType="actionModal.confirmType"
      :placeholder="actionModal.placeholder"
      @cancel="actionModal.isVisible = false"
      @confirm="handleModalConfirm"
    />

    <!-- Triggers -->
    <ActionTrigger
      dataKey="acceptSponsorship"
      ref="acceptTrigger"
      @success="onActionSuccess('Sponsorisation approuvée !')"
      @failed="onActionError"
      dispatchOnSuccess="SPONSORSHIP_UPDATED"
    />
    <ActionTrigger
      dataKey="rejectSponsorship"
      ref="rejectTrigger"
      @success="onActionSuccess('Sponsorisation rejetée !')"
      @failed="onActionError"
      dispatchOnSuccess="SPONSORSHIP_UPDATED"
    />
    <ActionTrigger
      dataKey="suggestCorrections"
      ref="suggestTrigger"
      @success="onActionSuccess('Suggestions envoyées !')"
      @failed="onActionError"
      dispatchOnSuccess="SPONSORSHIP_UPDATED"
    />
    <ActionTrigger
      dataKey="blockSponsorship"
      ref="blockTrigger"
      @success="onActionSuccess('Sponsorisation bloquée !')"
      @failed="onActionError"
      dispatchOnSuccess="SPONSORSHIP_UPDATED"
    />
    <ActionTrigger
      dataKey="unblockSponsorship"
      ref="unblockTrigger"
      @success="onActionSuccess('Sponsorisation débloquée !')"
      @failed="onActionError"
      dispatchOnSuccess="SPONSORSHIP_UPDATED"
    />
    <ActionTrigger
      dataKey="updateSponsorship"
      ref="updateTrigger"
      @success="onBannerUpdateSuccess"
      @failed="onActionError"
      dispatchOnSuccess="SPONSORSHIP_UPDATED"
    />
    <ActionTrigger
      dataKey="deleteSponsorship"
      ref="deleteTrigger"
      method="delete"
      @success="onDeleteSuccess"
      @failed="onActionError"
      dispatchOnSuccess="SPONSORSHIP_UPDATED"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import moment from 'moment'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import FileUploadWidget from '@/presentation/components/input/FileUploadWidget.vue'
import SponsorshipActionModal from './SponsorshipActionModal.vue'
import Commons from '@/shared/utils/commons'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'

const props = defineProps({
  sponsorshipData: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['delete'])

const acceptTrigger = ref(null)
const rejectTrigger = ref(null)
const suggestTrigger = ref(null)
const blockTrigger = ref(null)
const unblockTrigger = ref(null)
const updateTrigger = ref(null)
const deleteTrigger = ref(null)

const isEditing = ref(false)
const isEditingBanner = ref(false)
const editForm = ref({
  startDate: '',
  endDate: '',
  bannerImageUrl: ''
})

const actionModal = ref({
  isVisible: false,
  type: '', // 'accept', 'reject', 'suggest', 'block', 'unblock'
  title: '',
  subtitle: '',
  mode: 'confirm',
  confirmText: 'Confirmer',
  confirmType: 'primary',
  placeholder: ''
})

const startEditing = () => {
  editForm.value = {
    startDate: props.sponsorshipData?.startDate ? moment(props.sponsorshipData.startDate).format('YYYY-MM-DD') : '',
    endDate: props.sponsorshipData?.endDate ? moment(props.sponsorshipData.endDate).format('YYYY-MM-DD') : '',
    bannerImageUrl: props.sponsorshipData?.bannerImageUrl || ''
  }
  isEditing.value = true
}

const cancelEditing = () => {
  isEditing.value = false
}

const saveEditing = () => {
  const payload = {
    id: props.sponsorshipData?.id || '',
    startDate: editForm.value.startDate,
    endDate: editForm.value.endDate,
    bannerImageUrl: editForm.value.bannerImageUrl || props.sponsorshipData?.bannerImageUrl || ''
  }
  updateTrigger.value?.trigger(payload)
}

const statusLabel = computed(() => {
  const status = props.sponsorshipData?.status
  switch (status) {
    case 'active': return 'Actif'
    case 'approved': return 'Approuvé'
    case 'expired': return 'Expiré'
    case 'pending': return 'En attente'
    case 'rejected': return 'Rejeté'
    case 'blocked': return 'Bloqué'
    case 'correction_suggested': return 'Correction demandée'
    default: return status || 'Inconnu'
  }
})

const statusClasses = computed(() => {
  const status = props.sponsorshipData?.status
  if (status === 'active') return 'text-success-600 bg-success-50 border border-success-200'
  if (status === 'approved') return 'text-blue-600 bg-blue-50 border border-blue-200'
  if (status === 'expired') return 'text-gray-500 bg-gray-50 border border-gray-200'
  if (status === 'pending') return 'text-orange-600 bg-orange-50 border border-orange-200'
  if (status === 'rejected') return 'text-red-600 bg-red-50 border border-red-200'
  if (status === 'blocked') return 'text-red-600 bg-red-100 border border-red-300'
  if (status === 'correction_suggested') return 'text-amber-600 bg-amber-50 border border-amber-200'
  return 'text-gray-400 bg-gray-50 border border-gray-100'
})

/** @param {any} val */
const formatAmount = (val) => {
  return Commons.formatNumber(val, { suffix: ' F' })
}

/** @param {any} date */
const formatDate = (date) => {
  if (!date) return 'Non définie'
  return moment(date).format('DD MMMM YYYY')
}

const openActionModal = (type) => {
  actionModal.value.type = type
  actionModal.value.isVisible = true
  
  switch (type) {
    case 'accept':
      actionModal.value.title = 'Approuver le sponsoring'
      actionModal.value.subtitle = 'Êtes-vous sûr de vouloir approuver ce sponsoring ? Le restaurant pourra ensuite procéder au paiement.'
      actionModal.value.mode = 'confirm'
      actionModal.value.confirmText = 'Approuver'
      actionModal.value.confirmType = 'success'
      break
    case 'reject':
      actionModal.value.title = 'Rejeter le sponsoring'
      actionModal.value.subtitle = 'Veuillez indiquer la raison du rejet et les correctifs suggérées. Cette information sera transmise au restaurant.'
      actionModal.value.mode = 'input'
      actionModal.value.confirmText = 'Rejeter'
      actionModal.value.confirmType = 'danger'
      actionModal.value.placeholder = 'Ex: Image non conforme, dates invalides...'
      break
    case 'suggest':
      actionModal.value.title = 'Suggérer des corrections'
      actionModal.value.subtitle = 'Détaillez les modifications attendues pour que le restaurant puisse mettre à jour sa demande.'
      actionModal.value.mode = 'input'
      actionModal.value.confirmText = 'Envoyer les suggestions'
      actionModal.value.confirmType = 'primary'
      actionModal.value.placeholder = 'Ex: Veuillez recadrer l\'image ou changer la période...'
      break
    case 'block':
      actionModal.value.title = 'Bloquer le sponsoring'
      actionModal.value.subtitle = 'Cette action suspendra l\'affichage du sponsoring sur l\'application.'
      actionModal.value.mode = 'confirm'
      actionModal.value.confirmText = 'Bloquer l\'affichage'
      actionModal.value.confirmType = 'danger'
      break
    case 'unblock':
      actionModal.value.title = 'Débloquer le sponsoring'
      actionModal.value.subtitle = 'Le sponsoring sera de nouveau visible si la période est toujours valide.'
      actionModal.value.mode = 'confirm'
      actionModal.value.confirmText = 'Rétablir l\'affichage'
      actionModal.value.confirmType = 'success'
      break
    case 'delete':
      actionModal.value.title = 'Supprimer le sponsoring'
      actionModal.value.subtitle = 'Êtes-vous sûr de vouloir supprimer définitivement ce sponsoring ? Cette action est irréversible.'
      actionModal.value.mode = 'confirm'
      actionModal.value.confirmText = 'Supprimer définitivement'
      actionModal.value.confirmType = 'danger'
      break
  }
}

const handleModalConfirm = (value) => {
  actionModal.value.isVisible = false
  const id = props.sponsorshipData?.id

  switch (actionModal.value.type) {
    case 'accept':
      acceptTrigger.value?.trigger({ id })
      break
    case 'reject':
      rejectTrigger.value?.trigger({ id, reason: value })
      break
    case 'suggest':
      suggestTrigger.value?.trigger({ id, message: value })
      break
    case 'block':
      blockTrigger.value?.trigger({ id })
      break
    case 'unblock':
      unblockTrigger.value?.trigger({ id })
      break
    case 'delete':
      deleteTrigger.value?.trigger({ id })
      break
  }
}

/** @param {any} data */
const handleImageSaved = (data) => {
  if (data?.files && data.files.length > 0) {
    const bannerImageUrl = data.files[0].url
    if (bannerImageUrl) {
      editForm.value.bannerImageUrl = bannerImageUrl
      
      // Si on n'est pas en mode édition globale, on applique directement le changement
      if (!isEditing.value) {
        updateTrigger.value?.trigger({
          id: props.sponsorshipData?.id || '',
          bannerImageUrl
        })
        isEditingBanner.value = false
      }

      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Image enregistrée',
        body: 'Image téléversée avec succès.',
        type: 'success'
      })
    }
  }
}

const onBannerUpdateSuccess = () => {
  isEditing.value = false
  isEditingBanner.value = false
  onActionSuccess('Sponsorisation mise à jour avec succès !')
}

const onDeleteSuccess = () => {
  onActionSuccess('Sponsorisation supprimée !')
  emit('delete')
}

/** @param {any} msg */
const onActionSuccess = (msg) => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: msg,
    type: 'success'
  })
}

/** @param {any} err */
const onActionError = (err) => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: err?.message || 'Une erreur est survenue lors de la mise à jour.',
    type: 'error'
  })
}
</script>

<style scoped>
.title-color {
  color: #1f2937;
}

.animate-in {
  animation: animate-in 0.3s ease-out;
}

@keyframes animate-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
